// IMPORTANT:   Any changes to these bindings may need to be copied into Back Office as these bindings are duplicated between the two solutions.
import * as $ from "jquery";
import * as ko from "knockout";
import * as _ from "underscore";
import * as localisationHelper from "localisationHelper";

// Helpers
import "selectTwo";

// Models
import {
    DefaultSelectTwoModel, SelectTwoWithChildren, SelectTwoBindingParams, SelectTwoEventOptions,
    MultipleOptionSelectionRequest, SelectTwoTemplateSelection, SelectTwoTemplateResult
} from "Content/ts/Models/Shared/SelectTwo/selectTwoModels";

// Configuration
import { SelectTwoAjaxConfiguration } from "Content/ts/Shared/Controls/SelectTwo/Configuration/selectTwoAjaxConfiguration";

export const Deps = {
    LocalisationHelper: localisationHelper
};

class SelectTwoBindings {
    templateEngine: any;
    selectTwoElement: any;

    constructor() {
        // Purpose: Configures a hierarchical list where multiple items can be selected.
        ko.bindingHandlers.selectTwoMultipleWithChildren = {
            init: (element: any, valueAccessor: () => KnockoutObservableArray<SelectTwoWithChildren>, allBindingsAccessor: () => SelectTwoBindingParams) => {
                const dataSource = valueAccessor(),
                    allBindings = allBindingsAccessor();

                allBindings.allowMultiple = true;

                this.configureSelectContainer(element, dataSource, allBindings);
                this.reloadTriggers(element, valueAccessor(), allBindings);
                this.preselectOptionWithChildItems(dataSource(), allBindings);
            },
            update: (element: any, valueAccessor: () => KnockoutObservableArray<SelectTwoWithChildren>, allBindingsAccessor: () => SelectTwoBindingParams) => {
                const dataSource = valueAccessor(),
                    allBindings = allBindingsAccessor();

                this.reloadTriggers(element, valueAccessor(), allBindings);
                this.preselectOptionWithChildItems(dataSource(), allBindings);
            }
        };

        // Purpose: Configures a hierarchical list where only one item can be selected.
        ko.bindingHandlers.selectTwoSingleWithChildren = {
            init: (element: any, valueAccessor: () => KnockoutObservableArray<SelectTwoWithChildren>, allBindingsAccessor: () => SelectTwoBindingParams) => {
                const dataSource = valueAccessor(),
                    allBindings = allBindingsAccessor();

                allBindings.allowMultiple = false;

                this.configureSelectContainer(element, dataSource, allBindings);
            },
            update: (element: any, valueAccessor: () => KnockoutObservableArray<SelectTwoWithChildren>, allBindingsAccessor: () => SelectTwoBindingParams) => {
                const dataSource = valueAccessor(),
                    allBindings = allBindingsAccessor();

                this.reloadTriggers(element, valueAccessor(), allBindings);
                this.preselectOptionWithChildItems(dataSource(), allBindings);
            }
        };

        // Purpose: Configures a standard searchable list where only one item can be selected.
        ko.bindingHandlers.selectTwoSingle = {
            init: (element: any, valueAccessor: () => KnockoutObservableArray<DefaultSelectTwoModel>, allBindingsAccessor: () => SelectTwoBindingParams) => {
                const dataSource = valueAccessor(),
                    allBindings = allBindingsAccessor();

                allBindings.allowMultiple = false;

                this.configureSelectContainer(element, dataSource, allBindings);
            },
            update: (element: any, valueAccessor: () => KnockoutObservableArray<DefaultSelectTwoModel>, allBindingsAccessor: () => SelectTwoBindingParams) => {
                const dataSource = valueAccessor(),
                    allBindings = allBindingsAccessor();

                this.reloadTriggers(element, valueAccessor(), allBindings);
                this.defaultPreselectOption(dataSource(), allBindings);
            }
        };

        // Purpose: Configures a standard searchable list where multiple items can be selected.
        ko.bindingHandlers.selectTwoMultiple = {
            init: (element: any, valueAccessor: () => KnockoutObservableArray<DefaultSelectTwoModel>, allBindingsAccessor: () => SelectTwoBindingParams) => {
                const dataSource = valueAccessor(),
                    allBindings = allBindingsAccessor();

                allBindings.allowMultiple = true;

                this.configureSelectContainer(element, dataSource, allBindings);
            },
            update: (element: any, valueAccessor: () => KnockoutObservableArray<DefaultSelectTwoModel>, allBindingsAccessor: () => SelectTwoBindingParams) => {
                const dataSource = valueAccessor(),
                    allBindings = allBindingsAccessor();

                this.reloadTriggers(element, valueAccessor(), allBindings);
                this.defaultPreselectOption(dataSource(), allBindings);
            }
        };


        // Purpose:     New bindings allows searches to be processed via the Database rather than loading all the content
        //              at page or component load. This is especially useful for scenarios where a significant amount of
        //              data could be loaded (e.g.Clubs, Locations and Inventory Items).
        // Sources:     https://select2.org/data-sources/ajax
        //              https://select2.org/programmatic-control/add-select-clear-items
        ko.bindingHandlers.selectTwoAjax = {
            init: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
                const value = valueAccessor(), // Note: This can be either a knockout observable or observable array
                    allBindings = allBindingsAccessor(),
                    selectTwoSignals = allBindings.selectTwoSignals as SelectTwoAjaxConfiguration;

                ($(element)).select2({
                    ajax: {
                        delay: 500, // Rate limit the request to only fire once the user has stopped typing
                        cache: true,
                        url: allBindings.url,
                        data: (params: any) => {
                            return allBindings.searchParameters ? allBindings.searchParameters(params.term) : params.term;
                        },
                        processResults: (response: any[]) => {
                            return {
                                results: allBindings.mapResults ? allBindings.mapResults(response) : response
                            };
                        }
                    },
                    minimumInputLength: 3,
                    placeholder: allBindings.placeholder,
                    allowClear: allBindings.allowClear || false,
                    multiple: allBindings.allowMultiple || false,
                    templateSelection: (repo: SelectTwoTemplateSelection) => {
                        return repo.text;
                    },
                    templateResult: (repo: SelectTwoTemplateResult) => {
                        return repo.text;
                    }
                });

                $(element)
                    .on("select2:select", (event: any) => {
                        const eve: SelectTwoEventOptions = event;
                        const dataId: number = parseInt(eve.params.data.id);

                        if (ko.isObservable(value) && 'push' in value) {
                            (value as KnockoutObservableArray<number>).push(dataId);
                        } else {
                            (value as KnockoutObservable<number>)(dataId);
                        }
                    })
                    .on("select2:unselect", (event: any) => {
                        const eve: SelectTwoEventOptions = event;
                        const dataId: number = parseInt(eve.params.data.id);

                        if (ko.isObservable(value) && 'push' in value) {
                            (value as KnockoutObservableArray<number>).remove(dataId);
                        } else {
                            (value as KnockoutObservable<number>)(null);
                        }
                    })
                    .on("select2:clear", function () {
                        $(this).on("select2:opening.cancelOpen", function (event) {
                            // Note     :   Select2 default configuration is to immediately open the dropdown after clearing
                            //              the option. This implementation gets around their decision by preventing the default
                            //              behaviour of the plugin and ensuring the popup window is immediately closed. The
                            //              decision around this has been signed off by the Product Team.
                            // Source   :   https://github.com/select2/select2/issues/3320
                            //              https://github.com/select2/select2/issues/3320#issuecomment-524153140
                            event.preventDefault();
                            $(this).off("select2:opening.cancelOpen");
                        });
                    });

                value.subscribe((newValue?: number) => {
                    $(element).val(newValue).trigger('change');
                });

                if (selectTwoSignals != null) {
                    selectTwoSignals.selectSingleOption.add(this.selectSingleOption.bind({ selectTwoElement: element }));
                    selectTwoSignals.selectMultipleOptions.add(this.selectMultipleOptions.bind({ selectTwoElement: element }));
                }
            }
        };
    }

    // Note: Will ensure the provided list of items are manually inserted into the Select2 container.
    private selectMultipleOptions(request: MultipleOptionSelectionRequest[]) {
        const ele = $(this.selectTwoElement) as any;

        const options: HTMLOptionElement[] = [];

        _.each(request, (item: MultipleOptionSelectionRequest) => {
            options.push(new Option(item.Name, item.Id, false, true))
        });

        // Note         :   Select2 uses a caching mechanism to store any preselected options in memory so it can reduce calls the 
        //                  database when appropriate. However, this can sometimes cause issues when navigating in and out of the same
        //                  workflow as it may duplicate the selection when dealing with arrays. In order to counteract this it's simpler
        //                  to completely clear out the cache and reappend all the necessary selected options programatically.
        ele.empty();
        ele.append(options).trigger('change');
    }

    // Note: Will ensure the provided observable has been updated before manually inserting and selecting the value via the Select2 container.
    private selectSingleOption(valueToUpdate: KnockoutObservable<any>, selectOptionValue: any, selectOptionText: any, selectOption?: boolean) {
        const element = $(this.selectTwoElement),
            preselectOption = selectOption || false,
            optionText = ko.unwrap(selectOptionText),
            optionValue = ko.unwrap(selectOptionValue);

        valueToUpdate(optionValue);

        if (element.find(`option[value="${optionValue}"]`).length) {
            element.val(optionValue).trigger('change');
        } else {
            const newOption = new Option(optionText, optionValue, false, preselectOption);
            element.append(newOption).trigger('change');
        }
    }

    private preselectOptionWithChildItems(dataSource: SelectTwoWithChildren[], allBindings: SelectTwoBindingParams) {
        _.each(dataSource, (item: SelectTwoWithChildren) => {
            this.attemptValueToggle(item, allBindings);

            if (item.children && item.children.length > 0) {
                this.preselectOptionWithChildItems(item.children, allBindings);
            }
        });
    }

    private defaultPreselectOption(dataSource: DefaultSelectTwoModel[], allBindings: SelectTwoBindingParams) {
        _.each(dataSource, (item: DefaultSelectTwoModel) => {
            this.attemptValueToggle(item, allBindings);
        });
    }

    private attemptValueToggle(item: DefaultSelectTwoModel, allBindings: SelectTwoBindingParams) {
        const id = allBindings.idIsString ? item.id : parseInt(item.id);

        if (item.selected && !allBindings.selectedValues) {
            allBindings.selectedValue(id);
        }
    }

    // Note: Ensures the list is completely cleaned up before adding the new options. Needs to be done in this manor in order to stop duplicate 
    //       items from being added to the list.
    private reloadTriggers(element: any, dataSource: KnockoutObservableArray<any>, allBindings: SelectTwoBindingParams) {
        let ele = $(element) as any,
            dataAdapter = ele.data("select2").dataAdapter;

        ele.empty();

        // Note         :   Standard select markup requires a placeholder option in order to have a default unselected state and it turns out Select2 
        //                  works in the same way. If you specify a placeholder message it only acts as an overlay to the input and doesn't actually allow
        //                  an 'unselected' option even though you can clear the selected option from the UI. This becomes problematic when you have a search 
        //                  page (e.g.Sports Course Search) which shouldn't have an option pre-selected. With that in mind and their own documentation proving 
        //                  this they recommend manually adding in an 'unselected' option to the top of the list.
        // Technical    :   When the list updates (e.g. loading has finished) there is no 'unselected' state so rather than leaving it upto the calling element
        //                  to define this information, it's added manually. From the hidden select perspective the option is available, however in terms of select2
        //                  it's hidden from view and selection. Additionally, this issue only affects single selection containers so multi - selects don't need this 
        //                  'unselected' option.
        // Source       :   https://select2.org/placeholders
        //                  https://github.com/select2/select2/issues/3553
        if (allBindings.placeholderMessage && !allBindings.allowMultiple) {
            dataSource.unshift({ id: "", text: "" } as DefaultSelectTwoModel);
        }

        dataAdapter.addOptions(dataAdapter.convertToOptions(dataSource()));
        ele.trigger("change");
    }

    // Note: Base configuration for a select2 container, use this when adding new bindings to this file.
    private configureSelectContainer(element: any, dataSource: KnockoutObservableArray<any>, allBindings: SelectTwoBindingParams) {
        const ele = $(element) as any;

        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
            if (element.data != null && element.data("select2")) {
                element.select2("select2");
            }
        });

        ele.select2({
            data: dataSource,
            dropdownAutoWidth: false,
            allowClear: allBindings.allowClear,
            multiple: allBindings.allowMultiple,
            closeOnSelect: allBindings.closeOnSelect,
            placeholder: allBindings.placeholderMessage,
            minimumResultsForSearch: allBindings.minimumResultsForSearch
        }).on("select2:select", function (event: any) {
            // Note: The IDs assigned in select2 are strings so they need to be converted to ints in 
            //       order to remove them from the selectedValues list. Can also be strings when the binding 
            //       'idIsString' is set to true in which case the values don't need to be parsed as ints

            const eve: SelectTwoEventOptions = event;
            const selectedId = allBindings.idIsString
                ? eve.params.data.id
                : parseInt(eve.params.data.id);

            if (allBindings.selectedValues) {
                allBindings.selectedValues.push(selectedId);
            } else {
                const reevaluateValue: string | number = (function () {
                    if (allBindings.idIsString) {
                        return selectedId;
                    } else {
                        return selectedId >= 0
                            ? selectedId
                            : null;
                    }
                })();

                allBindings.selectedValue(reevaluateValue);
            }
        }).on("select2:unselect", function (event: any) {
            const eve: SelectTwoEventOptions = event;

            //Note: The IDs assigned in select2 are strings so they need to be converted to ints in 
            //      order to remove them from the selectedValues list.
            if (allBindings.selectedValues) {
                allBindings.selectedValues.remove(parseInt(eve.params.data.id));
            } else {
                allBindings.selectedValue(null);
            }
        });

        if (allBindings.selectedValues) {
            allBindings.selectedValues.subscribe((newValue: number[] | string[]) => {
                $(element).val(<any>newValue).trigger('change');
            });
        } else {
            allBindings.selectedValue.subscribe((newValue?: number | string) => {
                $(element).val(newValue).trigger('change');
            });
        }
    }
}

var service = new SelectTwoBindings();
export { service as SelectTwoBindings };